import { skipToken, useQuery } from '@tanstack/react-query';
import { memo, useEffect } from 'react';

import type * as api from '@gitbook/api-client';
import { colorContrast, colorScale, DEFAULT_TINT_COLOR, scale } from '@gitbook/colors';
import { assert } from '@gitbook/util-ts';

import {
    ContentEditorColorContext,
    type ContentEditorColorContextValue,
    DEFAULT_EDITOR_BACKGROUND_COLORS,
    DEFAULT_EDITOR_TEXT_COLORS,
    useSpacePrimarySite,
} from '../../../../Components';
import { apiQuery } from '../../../../state';

/**
 * Define semantic colors for the editor.
 */
const SEMANTIC_COLORS: ContentEditorColorContextValue = {
    groups: [
        {
            key: 'text',
            title: 'Text',
            kind: 'text',
            colors: [
                {
                    key: '$primary',
                    title: 'Primary',
                    color: 'var(--editor-color-text-primary)',
                },
                {
                    key: '$info',
                    title: 'Info',
                    color: 'var(--editor-color-text-info)',
                },
                {
                    key: '$success',
                    title: 'Success',
                    color: 'var(--editor-color-text-success)',
                },
                {
                    key: '$warning',
                    title: 'Warning',
                    color: 'var(--editor-color-text-warning)',
                },
                {
                    key: '$danger',
                    title: 'Danger',
                    color: 'var(--editor-color-text-danger)',
                },
                ...DEFAULT_EDITOR_TEXT_COLORS,
            ],
        },
        {
            key: 'background',
            title: 'Background',
            kind: 'background',
            colors: [
                {
                    key: '$primary',
                    title: 'Primary',
                    color: 'var(--editor-color-bg-primary)',
                },
                {
                    key: '$info',
                    title: 'Info',
                    color: 'var(--editor-color-bg-info)',
                },
                {
                    key: '$success',
                    title: 'Success',
                    color: 'var(--editor-color-bg-success)',
                },
                {
                    key: '$warning',
                    title: 'Warning',
                    color: 'var(--editor-color-bg-warning)',
                },
                {
                    key: '$danger',
                    title: 'Danger',
                    color: 'var(--editor-color-bg-danger)',
                },
                ...DEFAULT_EDITOR_BACKGROUND_COLORS,
            ],
        },
    ],
};

/**
 * Injects in the editor the colors from the site customization.
 */
export function SiteCustomizationProvider(props: { space: api.Space; children: React.ReactNode }) {
    const { space, children } = props;

    return (
        <ContentEditorColorContext.Provider value={SEMANTIC_COLORS}>
            <ColorInjector spaceId={space.id} organizationId={space.organization} />
            {children}
        </ContentEditorColorContext.Provider>
    );
}

/**
 * Fetches the site customization and injects the colors as global CSS variables.
 * This way we don't re-render the editor when the customization loads.
 */
const ColorInjector = memo(function ColorInjector(props: {
    spaceId: string;
    organizationId: string;
}) {
    const { spaceId, organizationId } = props;
    const orgAllContentResult = useQuery(apiQuery('getOrganizationAllContent', { organizationId }));
    const site = useSpacePrimarySite(orgAllContentResult.data ?? null, spaceId);
    const siteCustomizationResult = useQuery(
        apiQuery(
            'getSiteCustomizationById',
            site
                ? {
                      organizationId,
                      siteId: site.site.id,
                  }
                : skipToken
        )
    );
    const siteCustomization = siteCustomizationResult.data ?? null;
    useEffect(() => {
        if (!siteCustomization) {
            return;
        }
    }, [siteCustomization]);
    if (siteCustomization) {
        const customizationColors = [
            'primaryColor',
            'infoColor',
            'successColor',
            'warningColor',
            'dangerColor',
            'tint',
        ] as const;
        const scales = customizationColors.reduce(
            (acc, color) => {
                const customizationColor =
                    color === 'tint'
                        ? (siteCustomization.styling[color]?.color ?? {
                              dark: DEFAULT_TINT_COLOR,
                              light: DEFAULT_TINT_COLOR,
                          })
                        : siteCustomization.styling[color];
                acc[color] = {
                    light: colorScale(customizationColor.light),
                    dark: colorScale(customizationColor.dark, { darkMode: true }),
                };
                return acc;
            },
            {} as Record<(typeof customizationColors)[number], { light: string[]; dark: string[] }>
        );
        assert('DEFAULT' in scale.text, 'Expected scale.text to have a DEFAULT color');
        const textScale = scale.text.DEFAULT - 1;
        assert('DEFAULT' in scale.components, 'Expected scale.components to have a DEFAULT color');
        const bgScale = scale.components.DEFAULT - 1;
        assert('solid' in scale.accents, 'Expected scale.accents to have a solid color');
        assert(
            'solid-hover' in scale.accents,
            'Expected scale.accents to have a solid-hover color'
        );
        const accentSolidScale = scale.accents.solid - 1;
        const accentSolidHoverScale = scale.accents['solid-hover'] - 1;
        assert('DEFAULT' in scale.components, 'Expected scale.components to have a DEFAULT color');
        assert('hover' in scale.components, 'Expected scale.components to have a hover color');
        const componentScale = scale.components.DEFAULT - 1;
        const componentHoverScale = scale.components.hover - 1;
        return (
            <style>
                {`
                    html, .theme-color-light {
                        --editor-color-text-primary: ${scales.primaryColor.light[textScale]};
                        --editor-color-text-info: ${scales.infoColor.light[textScale]};
                        --editor-color-text-success: ${scales.successColor.light[textScale]};
                        --editor-color-text-warning: ${scales.warningColor.light[textScale]};
                        --editor-color-text-danger: ${scales.dangerColor.light[textScale]};

                        --editor-color-bg-primary: ${scales.primaryColor.light[bgScale]};
                        --editor-color-bg-info: ${scales.infoColor.light[bgScale]};
                        --editor-color-bg-success: ${scales.successColor.light[bgScale]};
                        --editor-color-bg-warning: ${scales.warningColor.light[bgScale]};
                        --editor-color-bg-danger: ${scales.dangerColor.light[bgScale]};

                        --editor-color-code-background: ${scales.tint.light[1]};
                        --editor-color-code-foreground: ${scales.tint.light[10]};
                        --editor-color-code-punctuation: ${scales.tint.light[10]};
                        --editor-color-code-link: ${scales.primaryColor.light[9]};
                        --editor-color-code-constant: ${scales.warningColor.light[9]};
                        --editor-color-code-string: ${scales.warningColor.light[9]};
                        --editor-color-code-string-expression: ${scales.successColor.light[9]};
                        --editor-color-code-keyword: ${scales.dangerColor.light[9]};
                        --editor-color-code-paramter: ${scales.warningColor.light[9]};
                        --editor-color-code-function: ${scales.primaryColor.light[9]};
                        --editor-color-code-inserted: ${scales.successColor.light[9]};
                        --editor-color-code-deleted: ${scales.dangerColor.light[9]};
                        --editor-color-code-changed: ${scales.tint.light[11]};

                        --button-user-primary-text-color: ${colorContrast(scales.primaryColor.light[accentSolidScale])};
                        --button-user-primary-bg-color: ${scales.primaryColor.light[accentSolidScale]};
                        --button-user-primary-bg-hover-color: ${scales.primaryColor.light[accentSolidHoverScale]};

                        --button-user-secondary-text-color: ${colorContrast(scales.tint.light[textScale])};
                        --button-user-secondary-bg-color: ${scales.tint.light[componentScale]};
                        --button-user-secondary-bg-hover-color: ${scales.tint.light[componentHoverScale]};
                    }
                        
                    .theme-color-dark {
                        --editor-color-text-primary: ${scales.primaryColor.dark[textScale]};
                        --editor-color-text-info: ${scales.infoColor.dark[textScale]};
                        --editor-color-text-success: ${scales.successColor.dark[textScale]};
                        --editor-color-text-warning: ${scales.warningColor.dark[textScale]};
                        --editor-color-text-danger: ${scales.dangerColor.dark[textScale]};

                        --editor-color-bg-primary: ${scales.primaryColor.dark[bgScale]};
                        --editor-color-bg-info: ${scales.infoColor.dark[bgScale]};
                        --editor-color-bg-success: ${scales.successColor.dark[bgScale]};
                        --editor-color-bg-warning: ${scales.warningColor.dark[bgScale]};
                        --editor-color-bg-danger: ${scales.dangerColor.dark[bgScale]};

                        --editor-color-code-background: ${scales.tint.dark[1]};
                        --editor-color-code-foreground: ${scales.tint.dark[10]};
                        --editor-color-code-punctuation: ${scales.tint.dark[10]};
                        --editor-color-code-link: ${scales.primaryColor.dark[9]};
                        --editor-color-code-constant: ${scales.warningColor.dark[10]};
                        --editor-color-code-string: ${scales.warningColor.dark[10]};
                        --editor-color-code-string-expression: ${scales.successColor.dark[10]};
                        --editor-color-code-keyword: ${scales.dangerColor.dark[10]};
                        --editor-color-code-paramter: ${scales.warningColor.dark[10]};
                        --editor-color-code-function: ${scales.primaryColor.dark[10]};
                        --editor-color-code-inserted: ${scales.successColor.dark[9]};
                        --editor-color-code-deleted: ${scales.dangerColor.dark[9]};
                        --editor-color-code-changed: ${scales.tint.dark[11]};

                        --button-user-primary-text-color: ${colorContrast(scales.primaryColor.dark[accentSolidScale])};
                        --button-user-primary-bg-color: ${scales.primaryColor.dark[accentSolidScale]};
                        --button-user-primary-bg-hover-color: ${scales.primaryColor.dark[accentSolidHoverScale]};

                        --button-user-secondary-text-color: ${colorContrast(scales.tint.dark[textScale])};
                        --button-user-secondary-bg-color: ${scales.tint.dark[componentScale]};
                        --button-user-secondary-bg-hover-color: ${scales.tint.dark[componentHoverScale]};
                    }
                `}
            </style>
        );
    }

    // If there is no site, we use the default editor colors.
    const colorNames = ['blue', 'orange', 'red', 'green', 'purple'] as const;
    const colors = colorNames.reduce(
        (acc, color) => {
            const text = DEFAULT_EDITOR_TEXT_COLORS.find((c) => c.key === color);
            assert(text, `Expected to find a text color for ${color}`);
            const bg = DEFAULT_EDITOR_BACKGROUND_COLORS.find((c) => c.key === color);
            assert(bg, `Expected to find a background color for ${color}`);
            acc[color] = {
                text: text.color,
                background: bg.color,
            };
            return acc;
        },
        {} as Record<(typeof colorNames)[number], { text: string; background: string }>
    );
    return (
        <style>
            {`
            :root {
                --editor-color-text-primary: ${colors.blue.text};
                --editor-color-text-info: ${colors.purple.text};
                --editor-color-text-success: ${colors.green.text};
                --editor-color-text-warning: ${colors.orange.text};
                --editor-color-text-danger: ${colors.red.text};

                --editor-color-bg-primary: ${colors.blue.background};
                --editor-color-bg-info: ${colors.purple.background};
                --editor-color-bg-success: ${colors.green.background};
                --editor-color-bg-warning: ${colors.orange.background};
                --editor-color-bg-danger: ${colors.red.background};
            }
            `}
        </style>
    );
});
